import React, { useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Avatar
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  People,
  Business,
  Work,
  LocalOffer,
  AttachMoney,
  Description,
  CardMembership,
  Category,
  AccountCircle,
  Logout,
  AdminPanelSettings,
  PhotoCamera,
  Payment,
  SmartToy
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const drawerWidth = 260;

const MainLayout = () => {
  const navigate = useNavigate();
  const { user, role, logout, hasRole } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleClose();
    await logout();
    navigate('/login');
  };

  const menuItems = [
    { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard', roles: null },
    { text: 'Admins', icon: <AdminPanelSettings />, path: '/admins', roles: ['super_admin', 'manager'] },
    { text: 'Employees', icon: <People />, path: '/employees', roles: null },
    { text: 'Employers', icon: <Business />, path: '/employers', roles: null },
    { text: 'Jobs', icon: <Work />, path: '/jobs', roles: ['super_admin'] },
    { text: 'Coupons', icon: <LocalOffer />, path: '/coupons', roles: null },
    { text: 'Commissions', icon: <AttachMoney />, path: '/commissions', roles: null },
    { text: 'CV Requests', icon: <Description />, path: '/cv-requests', roles: ['super_admin'] },
    { text: 'Profile Photos', icon: <PhotoCamera />, path: '/profile-photos', roles: ['super_admin'] },
    { text: 'Plans', icon: <CardMembership />, path: '/plans', roles: ['super_admin'] },
    { text: 'Orders & Payments', icon: <Payment />, path: '/orders', roles: ['super_admin', 'manager', 'staff'] },
    { text: 'Catalogs', icon: <Category />, path: '/catalogs', roles: ['super_admin', 'manager'] },
    { text: 'AI Logs', icon: <SmartToy />, path: '/ai-logs', roles: ['super_admin'] }
  ];

  const filteredMenuItems = menuItems.filter(item => {
    if (!item.roles) return true;
    return hasRole(item.roles);
  });

  const drawer = (
    <div>
      <Toolbar>
        <Typography variant="h6" noWrap component="div">
          Job Portal Admin
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {filteredMenuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton onClick={() => navigate(item.path)}>
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` }
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {user?.name || 'Admin Panel'}
          </Typography>
          <Typography variant="body2" sx={{ mr: 2 }}>
            {role?.replace('_', ' ').toUpperCase()}
          </Typography>
          <IconButton
            size="large"
            onClick={handleMenu}
            color="inherit"
          >
            <Avatar sx={{ width: 32, height: 32 }}>
              <AccountCircle />
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            <MenuItem disabled>
              <Typography variant="body2">{user?.email}</Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth }
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth }
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` }
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
};

export default MainLayout;
